/**
 * Shared storage helpers for promotional surface cooldowns.
 * Keys, durations, and storage kind come from PROMO_CONFIG.surfaces[surface].
 */

/** @typedef {'local' | 'session'} PromoStorageKind */

/**
 * @param {PromoStorageKind} [storage='local']
 * @returns {Storage | null}
 */
function resolveWebStorage(storage = 'local') {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return storage === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

/**
 * @param {string} storageKey
 * @param {PromoStorageKind} [storage='local']
 * @returns {number | null} Unix timestamp in milliseconds, or null if unset/invalid.
 */
export function getLastShownTimestamp(storageKey, storage = 'local') {
  if (!storageKey) {
    return null;
  }

  try {
    const area = resolveWebStorage(storage);
    if (!area) {
      return null;
    }

    const raw = area.getItem(storageKey);
    if (raw == null) {
      return null;
    }

    const timestamp = Number(raw);
    return Number.isFinite(timestamp) ? timestamp : null;
  } catch {
    return null;
  }
}

/**
 * Persists a promotional impression timestamp (cooldown starts on display).
 * @param {string} storageKey
 * @param {number} [timestamp=Date.now()]
 * @param {PromoStorageKind} [storage='local']
 */
export function setLastShownTimestamp(storageKey, timestamp = Date.now(), storage = 'local') {
  if (!storageKey) {
    return;
  }

  try {
    const area = resolveWebStorage(storage);
    if (!area) {
      return;
    }

    area.setItem(storageKey, String(timestamp));
  } catch {
    // Storage may be unavailable; promo can still display without persisting cooldown.
  }
}

/**
 * @param {object} options
 * @param {string} options.storageKey
 * @param {number} options.cooldownMs
 * @param {number} [options.now=Date.now()]
 * @param {PromoStorageKind} [options.storage='local']
 * @returns {boolean}
 */
export function isCooldownExpired({ storageKey, cooldownMs, now = Date.now(), storage = 'local' }) {
  if (cooldownMs <= 0) {
    return true;
  }

  const lastShown = getLastShownTimestamp(storageKey, storage);
  if (lastShown == null) {
    return true;
  }

  return now - lastShown >= cooldownMs;
}
